import React, { useRef, useEffect, useCallback } from 'react';
import { Tooltip as PTooltip } from 'primereact/tooltip';
import classNames from 'classnames';
import { ITooltip, TooltipEvent, TooltipPosition, TooltipType } from './Tooltip.types';
import styles from './Tooltip.module.scss';

const useTooltipTouchLongPress = (
  target: string | string[] | HTMLElement | React.RefObject<HTMLElement> | undefined,
  tooltipRef: React.RefObject<any>,
  delay: number = 300
) => {
  const touchTimer = useRef<NodeJS.Timeout | null>(null);

  const resolveTarget = useCallback(
    () => (typeof target === 'string' ? document.querySelector(target) : (target as HTMLElement)),
    [target]
  );

  const callTooltipMethod = useCallback(
    (method: 'show' | 'hide') => {
      if (tooltipRef.current && typeof tooltipRef.current[method] === 'function') {
        const el = resolveTarget();
        if (el) tooltipRef.current[method]({ currentTarget: el });
      }
    },
    [tooltipRef, resolveTarget]
  );

  const showTooltip = useCallback(() => callTooltipMethod('show'), [callTooltipMethod]);
  const hideTooltip = useCallback(() => callTooltipMethod('hide'), [callTooltipMethod]);

  useEffect(() => {
    if (!target) return;
    const element = resolveTarget();
    if (!element) return;

    const handleTouchStart = () => {
      touchTimer.current = setTimeout(() => showTooltip(), delay);
    };

    const handleTouchEnd = () => {
      if (touchTimer.current) clearTimeout(touchTimer.current);
      hideTooltip();
    };

    element.addEventListener('touchstart', handleTouchStart);
    element.addEventListener('touchend', handleTouchEnd);
    element.addEventListener('touchcancel', handleTouchEnd);

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchend', handleTouchEnd);
      element.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [target, showTooltip, hideTooltip, delay, resolveTarget]);
};

export const Tooltip: React.FC<ITooltip> = ({
  target,
  content,
  position = TooltipPosition.Top,
  event = TooltipEvent.Hover,
  showDelay = 0,
  hideDelay = 0,
  className = '',
  type = TooltipType.Info,
  style,
}) => {
  const tooltipRef = useRef<any>(null);

  const tooltipClass = classNames(className, {
    [styles['tooltip-info']]: type === TooltipType.Info,
    [styles['tooltip-error']]: type === TooltipType.Error,
    [styles['tooltip-success']]: type === TooltipType.Success,
    [styles['tooltip-warning']]: type === TooltipType.Warning,
    [styles['tooltip-no-icon']]: type === TooltipType.NoIcon,
  });

  useTooltipTouchLongPress(target, tooltipRef, 300);

  return (
    <PTooltip
      ref={tooltipRef}
      target={target}
      children={content}
      position={position}
      event={event}
      showDelay={showDelay}
      hideDelay={hideDelay}
      className={tooltipClass}
      style={style}
    />
  );
};
